/**
 * Sprint T-16.1 · Automation Center · QueueTab.
 *
 * Ejecuciones status=pending + retry (available_at pendiente).
 * Auto-refresh 10s (§17.4).
 */
import React, { useState } from "react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge, MetricCard, WidgetGrid } from "../advisor";
import { useExecutions } from "../../hooks/useAutomation";
import ExecutionsTable from "./shared/ExecutionsTable";
import { ExecutionDetailPanel } from "./shared/DetailPanels";

const PAGE_SIZE = 50;

export default function QueueTab() {
  const [pagePending, setPagePending] = useState(0);
  const [pageRetry, setPageRetry] = useState(0);
  const [selected, setSelected] = useState(null);

  const pending = useExecutions(
    { status: "pending", limit: PAGE_SIZE, skip: pagePending * PAGE_SIZE },
    { poll: 10000 }
  );
  const retry = useExecutions(
    { status: "retry", limit: PAGE_SIZE, skip: pageRetry * PAGE_SIZE },
    { poll: 10000 }
  );

  return (
    <section className="space-y-4" data-testid="automation-queue-tab">
      <WidgetGrid cols="md:grid-cols-2">
        <MetricCard
          label="Pending"
          value={pending.data?.total ?? "…"}
          hint="esperando slot de worker"
          testId="queue-pending-total"
        />
        <MetricCard
          label="Retry"
          value={retry.data?.total ?? "…"}
          hint="programadas con backoff"
          testId="queue-retry-total"
        />
      </WidgetGrid>

      <WidgetCard
        title="Pending"
        description="Ejecuciones listas para ser leased por el worker · orden priority desc + available_at asc."
        citations={["/api/automation/executions?status=pending"]}
        testId="widget-queue-pending"
      >
        {pending.isLoading && <LoadingState />}
        {pending.error && <EmptyState message="No se pudo cargar." />}
        {pending.data && (pending.data.items || []).length === 0 && (
          <EmptyState message="Sin ejecuciones en cola." testId="queue-pending-empty" />
        )}
        {pending.data && (pending.data.items || []).length > 0 && (
          <ExecutionsTable
            items={pending.data.items}
            total={pending.data.total}
            page={pagePending}
            pageSize={PAGE_SIZE}
            onPageChange={setPagePending}
            onSelectRow={(e) => setSelected(e.id)}
            testIdPrefix="queue-pending"
            fetching={pending.isFetching}
            columns={["job_name", "attempt", "trigger", "finished_at", "actor"]}
          />
        )}
      </WidgetCard>

      <WidgetCard
        title="Retry"
        description="Ejecuciones esperando backoff antes de re-encolarse."
        citations={["/api/automation/executions?status=retry"]}
        testId="widget-queue-retry"
      >
        {retry.isLoading && <LoadingState />}
        {retry.data && (retry.data.items || []).length === 0 && (
          <EmptyState message="Sin ejecuciones en retry." testId="queue-retry-empty" />
        )}
        {retry.data && (retry.data.items || []).length > 0 && (
          <ExecutionsTable
            items={retry.data.items}
            total={retry.data.total}
            page={pageRetry}
            pageSize={PAGE_SIZE}
            onPageChange={setPageRetry}
            onSelectRow={(e) => setSelected(e.id)}
            testIdPrefix="queue-retry"
            fetching={retry.isFetching}
            columns={["job_name", "attempt", "trigger", "finished_at", "actor"]}
          />
        )}
      </WidgetCard>

      {selected && <ExecutionDetailPanel executionId={selected} onClose={() => setSelected(null)} />}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["auto-refresh 10s", "lease atómico backend"]} />
      </footer>
    </section>
  );
}
